<template>
    <div class="container-fluid py-4 pb-5 mb-5">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h2 class="mb-0 text-primary h3">Reservation Analytics Dashboard</h2>
                <p class="text-muted">Comprehensive financial and operational reporting</p>
            </div>
        </div>
        <!-- Filters -->

        <div class="card mb-4 shadow-sm">
            <div class="card-body">
                 <div class="row mb-2" style="row-gap:12px;">
                    <div class="col-12 col-md-8">
                        <date-range-picker :auto-apply="autoApply" v-model="dateRange" :ranges="ranges" :opens="datepicker_opens" :append-to-body="append_to_body" class="custom-width-datepicker">
                            <template #input="picker" style="width: 100% !important;">
                                <p v-if="picker.startDate !==null && picker.endDate !==null">{{ formatDate(picker.startDate,'DD-MMM-YYYY') }} - {{ formatDate(picker.endDate,'DD-MMM-YYYY') }}</p>
                                <p v-else><i class="bi bi-calendar-event"></i>  Filter by Date</p>
                            </template>
                        </date-range-picker>
                    </div>
                    <div class="col-12 col-md-4">
                        <div class="d-flex flex-wrap" style="gap:8px;">
                               <a @click="generateReport" class="btn btn-outline-primary">Filter by date</a>
                    <button
                        @click="downloadPDF"
                        class="btn btn-primary"
                        :disabled="downloading"
                    >
                        <span v-if="downloading">
                        <span class="spinner-border spinner-border-sm mr-2"></span>
                        Generating PDF...
                        </span>
                        <span v-else>
                        <i class="fas fa-download mr-2"></i>
                        Download Report
                        </span>
                    </button>
                        </div>

                    </div>
            </div>

    </div>

    </div>


    <!-- Error Alert -->
    <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
      {{ error }}
      <button type="button" class="close" @click="error = null">
        <span>&times;</span>
      </button>
    </div>

    <!-- Report Content -->
    <div v-if="reportData">
         <div class="card shadow-sm">
            <div class="card-body text-center bg-white">
                <!-- KPI Cards -->
                <div class="row mb-4">
                    <div class="col-6 col-md-3 mb-3">

                            <h3 class="mb-0 text-dark">{{ formatNumber(reportData.kpis.total_reservations) }}</h3>
                            <hr/>
                        <p class="text-muted text-capitalize mb-2">Total Reservations</p>


                    </div>
                    <div class="col-6 col-md-3 mb-3">
                         <h3 class="mb-0 text-dark">{{ convertToLocalCurrency(reportData.kpis.total_revenue) }}</h3>
                             <hr/>
                        <p class="text-muted text-capitalize mb-2">Total Revenue</p>


                    </div>
                    <div class="col-6 col-md-3 mb-3">
                        <h3 class="mb-0 text-dark">{{ formatPercent(reportData.kpis.occupancy_rate) }}%</h3>
                            <hr/>
                        <p class="text-muted text-capitalize mb-2">Occupancy Rate</p>


                    </div>
                    <div class="col-6 col-md-3 mb-3">
                        <h3 class="mb-0 text-dark">{{ convertToLocalCurrency(reportData.kpis.adr) }}</h3>
                            <hr/>
                        <p class="text-muted text-capitalize mb-2">ADR</p>


                    </div>
                </div>

            <!-- Secondary KPIs -->
            <div class="row mb-4">
                <div class="col-6 col-md-3 mb-3">
                    <h3 class="mb-0 text-dark">{{ convertToLocalCurrency(reportData.kpis.net_revenue) }}</h3>
                        <hr/>
                    <p class="text-muted text-capitalize mb-2">Net Revenue</p>


                </div>
                <div class="col-6 col-md-3 mb-3">
                    <h3 class="mb-0 text-dark">{{ convertToLocalCurrency(reportData.kpis.outstanding_balance) }}</h3>
                        <hr/>
                    <p class="text-muted text-capitalize mb-2">Outstanding</p>


                </div>
                <div class="col-6 col-md-3 mb-3">
                   <h3 class="mb-0 text-dark">{{ convertToLocalCurrency(reportData.kpis.revpar) }}</h3>
                       <hr/>
                    <p class="text-muted text-capitalize mb-2">RevPAR</p>


                </div>
                <div class="col-6 col-md-3 mb-3">
                    <h3 class="mb-0 text-dark">{{ formatNumber(reportData.kpis.avg_length_of_stay, 1) }} nights</h3>
                        <hr/>
                    <p class="text-muted text-capitalize mb-2">Avg Length of Stay</p>


                </div>
            </div>
      </div>
      </div>

      <!-- Charts Row 1 -->
      <div class="row mb-4">
        <div class="col-md-6 mb-3">
          <div class="card shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0">Revenue Over Time</h5>
            </div>
            <div class="card-body">
              <canvas ref="revenueChart"></canvas>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <div class="card shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0">Reservations Over Time</h5>
            </div>
            <div class="card-body">
              <canvas ref="reservationsChart"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row 2 -->
      <div class="row mb-4">
        <div class="col-md-6 mb-3">
          <div class="card shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0">Payment Methods Distribution</h5>
            </div>
            <div class="card-body">
              <canvas ref="paymentChart" style="max-height:220px;"></canvas>

            </div>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <div class="card shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0">Room Type Revenue</h5>
            </div>
            <div class="card-body">
              <canvas ref="roomRevenueChart"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Tables -->
      <div class="row mb-4">
        <!-- Room Performance Table -->
        <div class="col-md-6 mb-3">
          <div class="card shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0">Room Type Performance</h5>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="bg-light">
                    <tr>
                      <th>Room Type</th>
                      <th class="text-center">Bookings</th>
                      <th class="text-center">Occupancy</th>
                      <th class="text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="room in reportData.room_performance" :key="room.room_type">
                      <td>{{ room.room_type }}</td>
                      <td class="text-center">{{ formatNumber(room.booking_count) }}</td>
                      <td class="text-center">{{ formatPercent(room.occupancy_rate) }}%</td>
                      <td class="text-right">{{ convertToLocalCurrency(room.revenue) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Method Breakdown Table -->
        <div class="col-md-6 mb-3">
          <div class="card shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0">Payment Method Breakdown</h5>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="bg-light">
                    <tr>
                      <th>Payment Method</th>
                      <th class="text-right">Amount</th>
                      <th class="text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(amount, method) in reportData.payment_methods" :key="method" v-if="amount > 0">
                      <td>{{ formatMethodName(method) }}</td>
                      <td class="text-right">{{ convertToLocalCurrency(amount) }}</td>
                      <td class="text-right">{{ formatPercent(getPaymentPercentage(amount)) }}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Download PDF Button -->

    </div>

    <!-- Empty State -->
    <div v-else-if="!loading" class="text-center py-5">
      <h4 class="text-muted">Select a date range and click "Generate Report" to view analytics</h4>
    </div>
  </div>
</template>

<script>
import { downloadFile } from '../../services/pdfDownload';
    import axios from 'axios';
    import Chart from 'chart.js/auto'; // Chart.js v3+
    import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";
    export default {
        name: 'ReservationDashboard',
        components: {
            DateRangePicker
        },
        data() {
            return {
                filters: {
                    start_date: this.getDefaultStartDate(),
                    end_date: this.getDefaultEndDate(),
                },
                dateRange: {
                    startDate: this.getDefaultStartDate(),
                    endDate: this.getDefaultEndDate(),
                },
                autoApply: true,
                append_to_body: true,
                datepicker_opens: 'right',
                ranges: false,
                reportData: null,
                loading: false,
                downloading: false,
                error: null,
                charts: {
                    revenue: null,
                    reservations: null,
                    payment: null,
                    roomRevenue: null,
                }
            };
        },
        methods: {
            getDefaultStartDate() {
                const date = new Date();
                date.setDate(1);
                return date.toISOString().split('T')[0];
            },
            getDefaultEndDate() {
                const date = new Date();
                return date.toISOString().split('T')[0];
            },
            async generateReport() {
                this.loading = true;
                this.error = null;
                try {
                    const response = await axios.get('/api/sales-analytics/reports/reservations', {
                        params: this.dateRange
                    });
                    this.reportData = response.data.data;
                    this.$nextTick(() => {
                        this.renderCharts();
                    });
                } catch (err) {
                    this.error = err.response?.data?.message || 'Failed to generate report. Please try again.';
                    console.error('Report error:', err);
                } finally {
                    this.loading = false;
                }
            },
            async downloadPDF() {
                this.downloading = true;
                this.error = null;
                const params = new URLSearchParams({
                    startDate: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
                    endDate: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
                });
                try {
                    await downloadFile(`/api/sales-analytics/reports/reservations/download?${params.toString()}`, 'reservation-report.pdf');
                } catch (err) {
                    this.error = 'Failed to download PDF. Please try again.';
                } finally {
                    this.downloading = false;
                }
            },
            renderCharts() {
                this.destroyCharts();
                this.renderRevenueChart();
                this.renderReservationsChart();
                this.renderPaymentChart();
                this.renderRoomRevenueChart();
            },
            destroyCharts() {
                Object.keys(this.charts).forEach(key => {
                    if (this.charts[key]) {
                        this.charts[key].destroy();
                        this.charts[key] = null;
                    }
                });
            },
            renderRevenueChart() {
                const ctx = this.$refs.revenueChart.getContext('2d');
                const data = this.reportData.charts.revenue_over_time;
                this.charts.revenue = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels: data.map(d => this.formatDate(d.date)),
                        datasets: [{
                            label: 'Revenue',
                            data: data.map(d => d.revenue),
                            borderColor: '#fd4719',
                            backgroundColor: 'rgba(40, 167, 69, 0.1)',
                            tension: 0.3,
                            fill: true
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        scales: {
                            y: {
                                ticks: {
                                    callback: (value) => 'KES ' + this.formatNumber(value)
                                }
                            }
                        },
                        plugins: {
                            tooltip: {
                                callbacks: {
                                    label: (context) => 'Revenue: KES ' + this.formatNumber(context.parsed.y)
                                }
                            }
                        }
                    }
                });
            },
            renderReservationsChart() {
                const ctx = this.$refs.reservationsChart.getContext('2d');
                const data = this.reportData.charts.reservations_over_time;
                this.charts.reservations = new Chart(ctx, {
                    type: 'bar',
                    data: {
                        labels: data.map(d => this.formatDate(d.date)),
                        datasets: [{
                            label: 'Reservations',
                            data: data.map(d => d.count),
                            backgroundColor: '#fd4719',
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: {
                                    stepSize: 1
                                }
                            }
                        }
                    }
                });
            },
         renderPaymentChart() {
    const ctx = this.$refs.paymentChart.getContext('2d');
    const methods = this.reportData.payment_methods;

    const labels = [];
    const values = [];


    const colors = [
        '#fd4719', // brand orange
        '#ff6a3d', // light orange
        '#e63f15', // deep orange
        '#2b2b2b', // charcoal
        '#000000', // pure black accent
    ];

    Object.keys(methods).forEach((method) => {
        if (methods[method] > 0) {
            labels.push(this.formatMethodName(method));
            values.push(methods[method]);
        }
    });

    this.charts.payment = new Chart(ctx, {
        type: 'doughnut', // cleaner than pie
        data: {
            labels,
            datasets: [{
                data: values,
                backgroundColor: colors,
                borderWidth: 0,
                hoverOffset: 6
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false, // allows size control
            cutout: '60%', // donut thickness (beauty level ↑)
            layout: {
                padding: 10
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        boxWidth: 12,
                        padding: 15,
                        font: {
                            size: 11
                        }
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const label = context.label || '';
                            const value = context.parsed || 0;
                            return label + ': KES ' + this.formatNumber(value);
                        }
                    }
                }
            }
        }
    });
},
            renderRoomRevenueChart() {
                const ctx = this.$refs.roomRevenueChart.getContext('2d');
                const data = this.reportData.room_performance;
                this.charts.roomRevenue = new Chart(ctx, {
                    type: 'bar',
                    data: {
                        labels: data.map(d => d.room_type),
                        datasets: [{
                            label: 'Revenue',
                            data: data.map(d => d.revenue),
                            backgroundColor: '#2b2b2b',
                        }]
                    },
                    options: {
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: true,
                        scales: {
                            x: {
                                ticks: {
                                    callback: (value) => 'KES ' + this.formatNumber(value)
                                }
                            }
                        },
                        plugins: {
                            tooltip: {
                                callbacks: {
                                    label: (context) => 'Revenue: KES ' + this.formatNumber(context.parsed.x)
                                }
                            }
                        }
                    }
                });
            },
            convertToLocalCurrency(value) {
                if (!value && value !== 0) return 'KES 0.00';
                return 'KES ' + parseFloat(value).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },
            formatNumber(value, decimals = 0) {
                if (!value && value !== 0) return '0';
                return parseFloat(value).toLocaleString('en-US', {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals
                });
            },
            formatPercent(value) {
                if (!value && value !== 0) return '0.0';
                return parseFloat(value).toFixed(1);
            },
            formatDate(dateString) {
                const date = new Date(dateString);
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });
            },
            formatMethodName(method) {
                return method.split('_').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ');
            },
            getPaymentPercentage(amount) {
                const total = Object.values(this.reportData.payment_methods).reduce((a, b) => a + b, 0);
                return total > 0 ? (amount / total * 100) : 0;
            }
        },
        async mounted() {
            await this.generateReport()
        },
        beforeDestroy() {
            this.destroyCharts();
        }
    };
</script>

<style scoped>
    .card {
        border: none;
        border-radius: 8px;
    }
    .card-header {
        border-bottom: 1px solid #e9ecef;
        padding: 1rem 1.25rem;
    }
    .shadow-sm {
        box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
    }
    .table th {
        font-weight: 600;
        font-size: 0.875rem;
        text-transform: capitalize;
        letter-spacing: 0.5px;
    }
    .table td {
        vertical-align: middle;
    }
    .btn-block {
        width: 100%;
    }
    h6.text-muted {
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.5px;
    }
</style>
